import { BaseApi, type Result } from './api-client';

export type HealthStatus = 'Healthy' | 'Degraded' | 'Unhealthy' | 'Unknown';

export type ComponentHealth = {
  status: HealthStatus;
  description: string;
  duration: number;
  data?: Record<string, unknown>;
  exception?: string;
};

export type HealthStatusResponse = {
  status: HealthStatus;
  version: string;
  environment: string;
  timestamp: string;
  totalDuration: number;
  components: Record<string, ComponentHealth>;
  systemInfo?: Record<string, unknown>;
};

export class HealthApi extends BaseApi {
  protected readonly baseUrl = '/api/health';

  async getHealthStatus(): Promise<Result<HealthStatusResponse>> {
    return this.get<HealthStatusResponse>('/basic', null, 'Failed to get health status');
  }

  async getBasicHealth(): Promise<Result<any>> {
    return this.get<any>('/', null, 'Failed to get basic health');
  }

  async verifyAdminPassword(password: string): Promise<Result<{ isValid: boolean }>> {
    return this.post<{ isValid: boolean }>(
      '/admin/verify-password',
      { password },
      'Failed to verify password'
    );
  }

  getStatusColor(status: HealthStatus): string {
    switch (status) {
      case 'Healthy':
        return '#22c55e';
      case 'Degraded':
        return '#f59e0b';
      case 'Unhealthy':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  }

  getStatusIcon(status: HealthStatus): string {
    switch (status) {
      case 'Healthy':
        return '✅';
      case 'Degraded':
        return '⚠️';
      case 'Unhealthy':
        return '❌';
      default:
        return '❓';
    }
  }

  formatDuration(duration: string | number | null | undefined): string {
    // Handle null/undefined
    if (duration == null) {
      return '0ms';
    }

    // Handle number input (milliseconds) - MUST check this first!
    if (typeof duration === 'number') {
      const totalMs = Math.round(duration);
      const totalSeconds = Math.floor(totalMs / 1000);
      const totalMinutes = Math.floor(totalSeconds / 60);
      const totalHours = Math.floor(totalMinutes / 60);

      const remainingMinutes = totalMinutes % 60;
      const remainingSeconds = totalSeconds % 60;
      const remainingMs = totalMs % 1000;

      if (totalHours > 0) {
        return `${totalHours}h ${remainingMinutes}m ${remainingSeconds}s`;
      }
      if (totalMinutes > 0) {
        return `${totalMinutes}m ${remainingSeconds}s`;
      }
      if (totalSeconds > 0) {
        return `${totalSeconds}.${remainingMs.toString().padStart(3, '0').slice(0, 3)}s`;
      }
      return `${totalMs}ms`;
    }

    // Handle string input (TimeSpan format: HH:MM:SS.mmm)
    // CRITICAL: Only call .match() if duration is actually a string
    if (typeof duration === 'string' && typeof duration.match === 'function') {
      const match = duration.match(/(\d+):(\d+):(\d+)\.(\d+)/);
      if (!match) {
        return duration;
      }

      const [, hours, minutes, seconds, milliseconds] = match;
      const totalMs = parseInt(milliseconds.slice(0, 3));
      const totalSeconds = parseInt(seconds);
      const totalMinutes = parseInt(minutes);
      const totalHours = parseInt(hours);

      if (totalHours > 0) {
        return `${totalHours}h ${totalMinutes}m ${totalSeconds}s`;
      }
      if (totalMinutes > 0) {
        return `${totalMinutes}m ${totalSeconds}s`;
      }
      if (totalSeconds > 0) {
        return `${totalSeconds}.${milliseconds.slice(0, 3)}s`;
      }
      return `${totalMs}ms`;
    }

    // Fallback for unexpected types - convert to string safely
    return String(duration ?? '0ms');
  }

  formatMemory(bytes: number): string {
    const mb = bytes / (1024 * 1024);
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  }
}

export const healthApi = new HealthApi();
